import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { OrderDetail, OrderItem, OrderItemResults, WorklistItem } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtDateTime, fmtTime, plural } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { ITEM_STATE_LABEL, itemTone } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Chip, Empty, Field, Input, Loading, PageHead, Segmented } from "../components/ui.js";
import { amendPayload, canBeCorrected, correctionBlock } from "../lib/correction.js";
import { DateRangeFilter, dateRangeQuery, describeDateRange, useDateRange } from "../components/DateRangeFilter.js";
import { Icon } from "../components/icons.js";
import { DraftPreviewButton } from "../components/DraftPreview.js";
import { ReturnSheet, validateAndRender, validatedMessage } from "./Validation.js";
import {
  CaptureHead,
  CaptureRows,
  ColourLegend,
  type Draft,
  capturableAnalytes,
  draftFrom,
  fillNormals,
  filledCount,
  focusFirstCaptureInput,
  hasEmptyOptionFields,
  isDirty,
  isLocked,
  capturePayload,
} from "../components/CaptureRows.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

/**
 * Captura: the chemist's worklist. A tube code opens that tube's study; a folio opens every study of
 * the order (Fernanda, 2026-11-02 19:18).
 *
 * Wide screens (> 1100 px) are master–detail like the lab's previous system (Fernanda's photo of
 * Toronja, 2026-11-02 20:41; Very 20:40 "solo los pacientes, y al picar..."): the patient list on the
 * left, amber while anything is still to capture and green once everything is captured, and the
 * whole-order capture of the selected patient on the right. The selection lives in `?orden=` so reload
 * and back keep it. Narrow screens (tablet portrait, phone) keep the collapsible list.
 */
export function CapturePage() {
  const branch = useBranch();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const wide = useWideLayout();
  const [state, setState] = useState<"pending" | "captured" | "all">("all");
  // The day's work by default, like the counter's old system (Very, 2026-11-02 18:37); older
  // pending studies, migrated ones included, stay one tap away.
  const [range, setRange] = useDateRange();
  const dates = dateRangeQuery(range);
  const [scan, setScan] = useState(params.get("folio") ?? "");
  const scanRef = useRef<HTMLInputElement>(null);
  const selectedId = params.get("orden");
  // Unsaved values in the capture pane: switching patient asks first.
  const dirty = useRef(false);
  // The patient list asks for every open study and filters by order, so a patient captured in the pane
  // turns green instead of leaving the list; the narrow list keeps filtering studies on the server.
  const serverState = wide || state === "all" ? undefined : state;
  // "Todos" (the default) lists every patient of the chosen days whatever the state of their studies,
  // validated ones included (the lab: «serían únicamente las del día», all of them, in order).
  const list = useQuery(
    () => get("worklist", { query: { branch_id: branch.id, ...(serverState ? { state: serverState } : { all_states: true }), ...dates, limit: 200 } }),
    [branch.id, serverState, dates.day, dates.from, dates.to],
  );

  const groups = useMemo(() => groupByOrder(list.data?.items ?? []), [list.data]);

  function select(orderId: string | null) {
    if (orderId === selectedId) return;
    if (dirty.current && !window.confirm("Hay valores sin guardar en este paciente. ¿Cambiar de paciente y descartarlos?")) return;
    dirty.current = false;
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.delete("folio");
      if (orderId) n.set("orden", orderId);
      else n.delete("orden");
      return n;
    });
  }

  async function openScan(raw: string) {
    // A tube label reads `FOLIO-seq` and the ticket reads `FOLIO`; both end in digits, so the code is
    // matched whole: first against the tubes (opens that tube's study), then against the folio.
    const code = raw.trim().toUpperCase();
    if (!code) return;
    try {
      const all = await get("worklist", { query: { branch_id: branch.id, limit: 200 } });
      const byTube = all.items.filter((i) => i.sample_label_code?.toUpperCase() === code);
      const hits = byTube.length ? byTube : all.items.filter((i) => i.order_folio.toUpperCase() === code);
      const target = hits.find((i) => i.state === "pending") ?? hits.find((i) => i.state === "captured") ?? hits[0];
      if (!target) {
        // Everything validated: the folio still opens the patient, where a study can be corrected (ADR 0022).
        const found = await get("orders", { query: { branch_id: branch.id, q: code, limit: 5 } });
        const order = found.items.find((o) => o.folio.toUpperCase() === code && o.state !== "cancelled");
        if (!order) {
          toast.error(`No hay estudios por capturar para ${code} en esta sucursal.`);
          return;
        }
        if (wide) select(order.id);
        else navigate(`/captura/solicitud/${order.id}`);
        return;
      }
      // The folio selects the patient in the capture pane on wide screens, the whole-order page otherwise.
      if (byTube.length) navigate(`/captura/${target.order_item_id}`);
      else if (wide) select(target.order_id);
      else navigate(`/captura/solicitud/${target.order_id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  useEffect(() => {
    const f = params.get("folio");
    if (f) void openScan(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const empty = (
    <Card>
      <Empty
        icon="flask"
        title={range.preset === "any" ? "Nada por capturar" : `Nada por capturar ${describeDateRange(range)}`}
        hint={range.preset === "any" ? "Cuando recepción registre una solicitud, sus estudios aparecerán aquí." : "Elige otras fechas o toca «Todos los días» para ver los estudios de días anteriores."}
      />
    </Card>
  );

  let listPane: ReactNode = null;
  if (wide) {
    listPane = <PatientList groups={groups} loading={list.loading && !list.data} filter={state} selectedId={selectedId} showDate={!dates.day} onSelect={select} empty={empty} />;
  } else if (!selectedId) {
    listPane =
      list.loading && !list.data ? (
        <Loading />
      ) : groups.length === 0 ? (
        empty
      ) : (
        <div className="stack-lg">
          {groups.map((items) => (
            <CaptureGroup key={items[0]!.order_id} items={items} showDate={!dates.day} />
          ))}
        </div>
      );
  }

  return (
    <>
      <PageHead kicker={branch.name} title="Captura de resultados" lede="Escanea el código del tubo o del ticket, o elige un paciente de la lista." />
      <div className="toolbar">
        <form
          className="scan-bar grow"
          style={{ maxWidth: 560 }}
          onSubmit={(e) => {
            e.preventDefault();
            void openScan(scan);
            setScan("");
          }}
        >
          <Icon name="scan" />
          <input {...NO_AUTOFILL} type="search" enterKeyHint="go" ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Escanear folio o código de tubo…" aria-label="Escanear folio" autoFocus autoComplete="off" />
          <Button size="sm" variant="ghost" type="submit">
            Abrir
          </Button>
        </form>
        <DateRangeFilter value={range} onChange={setRange} allowAny />
        <Segmented value={state} onChange={setState} options={[{ value: "all", label: "Todos" }, { value: "pending", label: "Por capturar" }, { value: "captured", label: "Capturados" }]} />
      </div>
      {/* Fixed child positions: the pane keeps its typed values when a rotation changes the layout. */}
      <div className={wide ? "capture-split" : undefined}>
        {listPane}
        {selectedId ? (
          <section className="capture-pane" aria-label="Área de captura">
            {!wide && (
              <div style={{ marginBottom: 12 }}>
                <Button variant="ghost" icon="chevronLeft" onClick={() => select(null)}>
                  Lista de pacientes
                </Button>
              </div>
            )}
            <OrderCapture key={selectedId} orderId={selectedId} embedded onChanged={() => void list.reload()} onDirtyChange={(d) => (dirty.current = d)} />
          </section>
        ) : wide ? (
          <section className="capture-pane" aria-label="Área de captura">
            <Card>
              <Empty icon="flask" title="Elige un paciente" hint="Toca un paciente de la lista para capturar todos sus estudios aquí." />
            </Card>
          </section>
        ) : null}
      </div>
    </>
  );
}

/** True above 1100 px, where the shell keeps its full sidebar; follows a rotation. */
function useWideLayout(): boolean {
  const query = "(min-width: 1101px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return wide;
}

/** One group per order, oldest registration first (folio breaks ties), the way the lab reads the day. */
export function groupByOrder(items: readonly WorklistItem[]): WorklistItem[][] {
  const m = new Map<string, WorklistItem[]>();
  for (const it of items) m.set(it.order_id, [...(m.get(it.order_id) ?? []), it]);
  return [...m.values()].sort(compareGroups);
}

function compareGroups(a: WorklistItem[], b: WorklistItem[]): number {
  return a[0]!.registered_at.localeCompare(b[0]!.registered_at) || a[0]!.order_folio.localeCompare(b[0]!.order_folio);
}

/** "pending" while any study of the order is still to capture; "done" once all are captured or validated. */
export function orderProgress(items: readonly WorklistItem[]): "pending" | "done" {
  return items.some((i) => i.state === "pending") ? "pending" : "done";
}

/**
 * Lista de pacientes (Toronja's left column): one row per order, amber while anything is pending,
 * green when everything is captured. The filter applies per order; the selected patient stays listed
 * once it is captured, and once it leaves the worklist entirely (validated), as done.
 */
function PatientList({
  groups,
  loading,
  filter,
  selectedId,
  showDate,
  onSelect,
  empty,
}: {
  groups: WorklistItem[][];
  loading: boolean;
  filter: "pending" | "captured" | "all";
  selectedId: string | null;
  showDate: boolean;
  onSelect: (orderId: string) => void;
  empty: ReactNode;
}) {
  const seen = useRef(new Map<string, WorklistItem[]>());
  for (const g of groups) seen.current.set(g[0]!.order_id, g);
  const rows = groups.map((items) => ({ items, progress: orderProgress(items) }));
  if (selectedId && !rows.some((r) => r.items[0]!.order_id === selectedId)) {
    const gone = seen.current.get(selectedId);
    if (gone) rows.push({ items: gone, progress: "done" });
  }
  rows.sort((a, b) => compareGroups(a.items, b.items));
  const visible = rows.filter((r) => filter === "all" || r.items[0]!.order_id === selectedId || r.progress === (filter === "pending" ? "pending" : "done"));
  if (loading) return <Loading />;
  if (visible.length === 0) return <div className="capture-patients">{empty}</div>;
  return (
    <Card
      className="capture-patients"
      pad={false}
      title={
        <div className="row" style={{ gap: 10, alignItems: "baseline" }}>
          <h2 className="h3">Lista de pacientes</h2>
          <span className="muted small">{plural(visible.length, "paciente", "pacientes")}</span>
        </div>
      }
    >
      <ul className="list capture-patient-list" aria-label="Lista de pacientes">
        {visible.map(({ items, progress }) => {
          const first = items[0]!;
          const active = first.order_id === selectedId;
          return (
            <li key={first.order_id}>
              <button type="button" className={`capture-patient ${progress}${active ? " active" : ""}`} data-progress={progress} aria-pressed={active} onClick={() => onSelect(first.order_id)}>
                {/* Folio and time on top, the full name on its own line (Fernanda, 2026-11-02 21:00): migrated
                    folios are 12 characters and squeezed the name into a letter-wide column. */}
                <span className="cp-line cp-top">
                  <span className="folio">{first.order_folio}</span>
                  <span className="cp-time tnum">{showDate ? fmtDateTime(first.registered_at) : fmtTime(first.registered_at)}</span>
                </span>
                <span className="cp-name">{first.patient_name}</span>
                <span className="cp-line secondary">
                  <span>
                    {first.patient_age_text} · {first.patient_sex} · {plural(items.length, "estudio", "estudios")}
                  </span>
                  <span className="sr-only">{progress === "pending" ? "Por capturar" : "Capturado"}</span>
                  <span className="cp-chips">
                    {items.some((i) => i.sample_pending) && (
                      <Chip tone="red" dot>
                        Muestra pendiente
                      </Chip>
                    )}
                    {first.balance_cents > 0 && <Chip tone="amber">Adeudo</Chip>}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * One order in the worklist: only the patient until tapped, then its studies (Very, 2026-11-02 20:40:
 * "que no salgan los estudios, solo los pacientes, y al picar ya se extienda"). «Capturar todo» keeps
 * the whole-order screen one tap away (Fernanda, 19:18).
 */
function CaptureGroup({ items, showDate }: { items: WorklistItem[]; showDate: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const first = items[0]!;
  return (
    <Card
      title={
        <button
          type="button"
          className="row wrap capture-group-toggle"
          style={{ gap: 12 }}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          title={open ? "Ocultar estudios" : "Ver estudios"}
        >
          <Icon name="chevronRight" className={`chev${open ? " open" : ""}`} style={{ color: "var(--ink-3)" }} />
          <span className="folio">{first.order_folio}</span>
          <span style={{ fontWeight: 600 }}>{first.patient_name}</span>
          <span className="muted small">{showDate ? fmtDateTime(first.registered_at) : fmtTime(first.registered_at)}</span>
          <span className="muted small">{plural(items.length, "estudio", "estudios")}</span>
          {items.some((i) => i.sample_pending) && (
            <Chip tone="red" dot>
              Muestra pendiente
            </Chip>
          )}
          {first.balance_cents > 0 && <Chip tone="amber">Adeudo</Chip>}
        </button>
      }
      actions={
        <span className="row" style={{ gap: 14 }}>
          <Link to={`/captura/solicitud/${first.order_id}`} className="small" style={{ fontWeight: 600 }} title="Capturar todos los estudios de la solicitud">
            Capturar todo
          </Link>
          <Link to={`/solicitudes/${first.order_id}`} className="muted small">
            Ver solicitud
          </Link>
        </span>
      }
      pad={false}
    >
      {open &&
        items.map((it) => (
          <button key={it.order_item_id} type="button" className="list-row clickable" onClick={() => navigate(`/captura/${it.order_item_id}`)}>
            <span className="mono muted" style={{ width: 100 }}>
              {it.study_clave}
            </span>
            <span className="grow">
              <div className="primary">{it.study_name}</div>
              <div className="secondary">
                {it.area_name ?? "Sin área"}
                {it.sample_label_code ? ` · tubo ${it.sample_label_code}` : ""}
              </div>
            </span>
            {it.sample_pending && <Chip tone="red">Sin muestra</Chip>}
            <Chip tone={itemTone(it.state)}>{ITEM_STATE_LABEL[it.state]}</Chip>
            <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
          </button>
        ))}
    </Card>
  );
}

/** One study: analyte rows with the applied interval and a colour that reacts while typing. Enter/Tab move down. */
export function CaptureItemPage() {
  const { itemId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const branch = useBranch();
  const lowRed = useSession().me?.lab_settings?.capture_low_red ?? false;
  const data = useQuery(() => get("orderItemResults", { params: { id: itemId } }), [itemId]);
  const [draft, setDraft] = useState<Draft>({});
  const [prefilled, setPrefilled] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState<OrderItemResults | null>(null);
  const canValidate = useCanValidate();
  const scope = useRef<HTMLDivElement>(null);
  const baseline = useMemo(() => (data.data ? draftFrom(data.data) : {}), [data.data]);

  useEffect(() => {
    if (!data.data) return;
    setDraft(draftFrom(data.data));
    setPrefilled(new Set());
    // Cleared on unmount: a late timer must not pull the focus into another screen (or the next pane).
    const t = setTimeout(() => focusFirstCaptureInput(scope.current ?? document), 50);
    return () => clearTimeout(t);
  }, [data.data]);

  // Reloaded per item: the route param changes without remounting, and "y siguiente" must follow the
  // order's studies that are still pending, not a list fetched before the previous ones were captured.
  const siblings = useQuery(() => get("worklist", { query: { branch_id: branch.id, state: "pending", limit: 200 } }), [branch.id, itemId]);

  if (data.loading && !data.data) return <Loading />;
  if (!data.data) return <Empty icon="warning" title="No se encontró el estudio" hint={data.error ?? undefined} />;
  const r = data.data;
  const locked = isLocked(r);
  const capturable = capturableAnalytes(r);
  const filled = filledCount(r, draft);
  const next = (siblings.data?.items ?? []).filter((i) => i.order_id === r.order_id && i.order_item_id !== r.order_item_id && i.state === "pending");
  const step = validationStep(r, draft, baseline);

  /** Validar where the values are (Very, 2026-11-02 19:25): saves first when the screen has unsaved values. */
  async function validateHere() {
    setBusy(true);
    try {
      let current = r;
      if (step === "save-and-validate") {
        const payload = capturePayload(r, draft);
        if (payload) current = await run<"results.capture", OrderItemResults>("results.capture", payload, { branch_id: branch.id });
        data.set(current);
      }
      const { allValidated, artifact } = await validateAndRender(r.order_id, [current], branch.id);
      toast.success(`${r.study_clave}: ${validatedMessage(1, allValidated, artifact)}`);
      await data.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function save(markCaptured: boolean) {
    setBusy(true);
    try {
      const payload = capturePayload(r, draft, markCaptured);
      if (!payload) {
        toast.error("Captura al menos un valor.");
        return;
      }
      const updated = await run<"results.capture", OrderItemResults>("results.capture", payload, { branch_id: branch.id });
      data.set(updated);
      toast.success(markCaptured ? `${r.study_clave} capturado. Pasa a validación.` : "Avance guardado.");
      if (markCaptured) {
        const n = next[0];
        if (n) navigate(`/captura/${n.order_item_id}`);
        else navigate("/captura");
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        kicker={
          <>
            <Link to={`/solicitudes/${r.order_id}`} style={{ color: "inherit" }}>
              {r.order_folio}
            </Link>{" "}
            · {r.study_clave}
          </>
        }
        title={r.study_name}
        lede={
          <span className="row wrap">
            <Chip tone={itemTone(r.state)} large>
              {ITEM_STATE_LABEL[r.state]}
            </Chip>
            {r.validation && (
              <span className="muted">
                Validado por {r.validation.user_name}
                {r.validation.cedula ? ` · Céd. ${r.validation.cedula}` : ""} · {fmtDateTime(r.validation.occurred_at)}
              </span>
            )}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/captura")}>
              Lista de trabajo
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/captura/solicitud/${r.order_id}`)}>
              Toda la solicitud
            </Button>
            {locked && r.state !== "cancelled" && (
              <Button variant="secondary" icon="send" onClick={() => navigate(`/entrega/${r.order_id}`)}>
                Entrega
              </Button>
            )}
          </>
        }
      />
      {locked && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="green">
            {canValidate && canBeCorrected(r)
              ? "Este estudio ya está validado. Para corregirlo, abre «Toda la solicitud» y toca «Corregir resultado» en el estudio."
              : "Este estudio ya está validado. Solo quien valida puede corregirlo."}
          </Alert>
        </div>
      )}
      <div ref={scope} data-capture-scope="">
        <Card
          pad={false}
          actions={
            !locked && hasEmptyOptionFields(r, draft) ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const res = fillNormals(r, draft);
                  setDraft(res.draft);
                  setPrefilled((s) => new Set([...s, ...res.filled]));
                  toast.success(normalsMessage(res.filled.length));
                }}
              >
                Llenar normales
              </Button>
            ) : undefined
          }
        >
          <CaptureHead />
          <CaptureRows
            r={r}
            draft={draft}
            locked={locked}
            prefilled={prefilled}
            lowRed={lowRed}
            onChange={(id, v) => {
              setDraft((d) => ({ ...d, [id]: v }));
              setPrefilled((s) => without(s, id));
            }}
          />
        </Card>
      </div>
      <div className="action-bar">
        <span className="muted small">
          {plural(filled, "valor capturado", "valores capturados")} de {capturable.length} · <ColourLegend lowRed={lowRed} />
        </span>
        <span className="grow" />
        {next.length > 0 && <span className="muted small">Siguen: {next.map((n) => n.study_clave).join(", ")}</span>}
        {!locked && (
          <>
            <Button variant="secondary" busy={busy} onClick={() => void save(false)} disabled={filled === 0 && !isDirty(r, draft, baseline)}>
              Guardar avance
            </Button>
            <Button variant={canValidate ? "secondary" : "primary"} icon="check" busy={busy} onClick={() => void save(true)} disabled={filled < capturable.length}>
              Capturado{next.length ? " y siguiente" : ""}
            </Button>
            {canValidate && r.state === "captured" && step === "validate" && (
              <Button variant="ghost" icon="refresh" disabled={busy} onClick={() => setReturning(r)}>
                Regresar a captura
              </Button>
            )}
            {canValidate && (
              <Button variant="primary" icon="badge" busy={busy} disabled={step === null} onClick={() => void validateHere()}>
                {validateLabel(r, draft, baseline)}
              </Button>
            )}
          </>
        )}
      </div>
      <ReturnSheet item={returning} onClose={() => setReturning(null)} onDone={() => void data.reload()} />
    </>
  );
}

/** True when the signed-in user may sign results (`can_validate`). */
function useCanValidate(): boolean {
  return !!useSession().me?.user.can_validate;
}

/**
 * What the Validar button of one study does: validate the stored values, save the screen's values and
 * then validate them, or nothing yet (incomplete, or not captured). Validated studies are locked and
 * never get here.
 */
export function validationStep(r: OrderItemResults, draft: Draft, baseline: Draft): "validate" | "save-and-validate" | null {
  if (isLocked(r)) return null;
  const complete = filledCount(r, draft) === capturableAnalytes(r).length;
  if (isDirty(r, draft, baseline) || r.state === "pending") return complete ? "save-and-validate" : null;
  return r.state === "captured" || r.state === "amended" ? "validate" : null;
}

/** The button's words: "Guardar y validar" whenever validating would first save the screen's values. */
function validateLabel(r: OrderItemResults, draft: Draft, baseline: Draft): string {
  return r.state === "pending" || isDirty(r, draft, baseline) ? "Guardar y validar" : "Validar";
}

function without(s: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!s.has(id)) return s;
  const n = new Set(s);
  n.delete(id);
  return n;
}

function normalsMessage(n: number): string {
  return `${plural(n, "campo llenado", "campos llenados")} con su valor normal. Revísalos antes de guardar.`;
}

/** A study of the order as the whole-order screen shows it. */
export interface OrderCaptureSection {
  item: OrderItem;
  /** Null while its tube is pending or rejected: the section shows "Sin muestra" instead of rows. */
  results: OrderItemResults | null;
  sampleMissing: "pending" | "rejected" | null;
}

/** The order and its studies (cancelled ones left out), each with its values or its missing tube. */
export async function loadOrderCapture(orderId: string): Promise<{ order: OrderDetail; sections: OrderCaptureSection[] }> {
  const order = await get("order", { params: { id: orderId } });
  const sampleByCode = new Map(order.samples.map((s) => [s.label_code, s]));
  const sections = await Promise.all(
    order.items
      .filter((it) => it.state !== "cancelled")
      .map(async (item): Promise<OrderCaptureSection> => {
        const s = item.sample_label_code ? sampleByCode.get(item.sample_label_code) : undefined;
        const missing = s && (s.state === "pending" || s.state === "rejected") ? s.state : null;
        // results.capture answers not-ready while the tube is pending or rejected, so a still-pending
        // study shows the "Sin muestra" block instead of inputs; anything already captured shows its values.
        const results = missing && item.state === "pending" ? null : await get("orderItemResults", { params: { id: item.id } });
        return { item, results, sampleMissing: results ? null : missing };
      }),
  );
  return { order, sections };
}

/**
 * Every study of one order on a single page (Fernanda, 2026-11-02 19:18: "que al seleccionar el
 * paciente se puedan editar todos los estudios seguidos"). Enter/Tab walk the analytes across
 * studies; "Guardar todo" sends one `results.capture` per study with changes, one after the other,
 * and a failure stays in its section with the typed values intact.
 */
export function CaptureOrderPage() {
  const { orderId = "" } = useParams();
  return <OrderCapture orderId={orderId} />;
}

/**
 * The whole-order capture: its own page at /captura/solicitud/:orderId, or the right pane of Captura's
 * patient list (`embedded`), where `onChanged` reloads the list after a save, validation or return and
 * `onDirtyChange` says whether the pane holds unsaved values.
 */
export function OrderCapture({
  orderId,
  embedded = false,
  onChanged,
  onDirtyChange,
}: {
  orderId: string;
  embedded?: boolean;
  onChanged?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const branch = useBranch();
  const lowRed = useSession().me?.lab_settings?.capture_low_red ?? false;
  const data = useQuery(() => loadOrderCapture(orderId), [orderId]);
  const [results, setResults] = useState<Record<string, OrderItemResults>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [baselines, setBaselines] = useState<Record<string, Draft>>({});
  const [failures, setFailures] = useState<Record<string, string>>({});
  const [prefilled, setPrefilled] = useState<Record<string, ReadonlySet<string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [returning, setReturning] = useState<OrderItemResults | null>(null);
  // Validated studies open for correction, each with the reason typed so far (ADR 0022).
  const [correcting, setCorrecting] = useState<Record<string, string>>({});
  const canValidate = useCanValidate();
  const scope = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data.data) return;
    setCorrecting({});
    const res: Record<string, OrderItemResults> = {};
    const d: Record<string, Draft> = {};
    for (const s of data.data.sections) {
      if (!s.results) continue;
      res[s.item.id] = s.results;
      d[s.item.id] = draftFrom(s.results);
    }
    setResults(res);
    setDrafts(d);
    setBaselines(d);
    setFailures({});
    setPrefilled({});
    // Cleared on unmount: a late timer must not pull the focus into another screen (or the next pane).
    const t = setTimeout(() => focusFirstCaptureInput(scope.current ?? document), 50);
    return () => clearTimeout(t);
  }, [data.data]);

  const dirty = (data.data?.sections ?? []).some((s) => {
    const r = results[s.item.id];
    return !!r && (!isLocked(r) || s.item.id in correcting) && isDirty(r, drafts[s.item.id] ?? {}, baselines[s.item.id] ?? {});
  });
  const dirtyChange = useRef(onDirtyChange);
  dirtyChange.current = onDirtyChange;
  useEffect(() => {
    dirtyChange.current?.(dirty);
  }, [dirty]);
  useEffect(() => () => dirtyChange.current?.(false), []);
  const changed = () => onChanged?.();

  if (data.loading && !data.data) return <Loading />;
  if (!data.data) return <Empty icon="warning" title="No se encontró la solicitud" hint={data.error ?? undefined} />;
  const { order, sections } = data.data;
  const p = order.patient;
  const patientName = [p.paternal_surname, p.maternal_surname, p.first_name].filter(Boolean).join(" ");

  function needsSave(id: string): boolean {
    const r = results[id];
    if (!r || isLocked(r)) return false;
    const draft = drafts[id] ?? {};
    const filled = filledCount(r, draft);
    // A note, or an antibiotic taken off, is worth saving even with nothing else typed (ADR 0019).
    if (filled === 0) return isDirty(r, draft, baselines[id] ?? {}) && capturePayload(r, draft) !== null;
    // A complete study saved earlier as progress still needs its "capturado" mark.
    return isDirty(r, draft, baselines[id] ?? {}) || (r.state === "pending" && filled === capturableAnalytes(r).length);
  }

  /** One `results.capture`; the saved study when it was applied. The typed values stay on screen when it fails. */
  async function saveOne(id: string): Promise<OrderItemResults | null> {
    const r = results[id]!;
    try {
      const payload = capturePayload(r, drafts[id] ?? {});
      if (!payload) return r;
      const updated = await run<"results.capture", OrderItemResults>("results.capture", payload, { branch_id: branch.id });
      const d = draftFrom(updated);
      setResults((m) => ({ ...m, [id]: updated }));
      setDrafts((m) => ({ ...m, [id]: d }));
      setBaselines((m) => ({ ...m, [id]: d }));
      setPrefilled((m) => ({ ...m, [id]: new Set() }));
      setFailures((m) => {
        const rest = { ...m };
        delete rest[id];
        return rest;
      });
      return updated;
    } catch (e) {
      setFailures((m) => ({ ...m, [id]: errorMessage(e) }));
      return null;
    }
  }

  function stepOf(id: string): ReturnType<typeof validationStep> {
    const r = results[id];
    return r ? validationStep(r, drafts[id] ?? {}, baselines[id] ?? {}) : null;
  }

  /**
   * Validar from the capture screen (Very, 2026-11-02 19:25): studies with unsaved values are saved
   * first, then everything that saved goes in one `results.validate` with the hashes just shown, and the
   * report is rendered as Validación does. Other sections' unsaved values are left alone.
   */
  async function validateSections(ids: string[], busyKey: string) {
    if (!ids.length) return;
    setBusy(busyKey);
    try {
      const ready: OrderItemResults[] = [];
      const failed: string[] = [];
      for (const id of ids) {
        if (stepOf(id) === "save-and-validate") {
          const saved = await saveOne(id);
          if (saved && (saved.state === "captured" || saved.state === "amended")) ready.push(saved);
          else failed.push(results[id]!.study_clave);
        } else ready.push(results[id]!);
      }
      if (!ready.length) {
        toast.error(`No se validó: ${failed.join(", ")}. Revisa el mensaje en su sección.`);
        return;
      }
      const { allValidated, artifact } = await validateAndRender(order.id, ready, branch.id);
      // Only the validated sections are refreshed, so values typed elsewhere stay on screen.
      const fresh = await Promise.all(ready.map((r) => get("orderItemResults", { params: { id: r.order_item_id } })));
      setResults((m) => ({ ...m, ...Object.fromEntries(fresh.map((f) => [f.order_item_id, f])) }));
      const freshDrafts = Object.fromEntries(fresh.map((f) => [f.order_item_id, draftFrom(f)]));
      setDrafts((m) => ({ ...m, ...freshDrafts }));
      setBaselines((m) => ({ ...m, ...freshDrafts }));
      const message = validatedMessage(ready.length, allValidated, artifact);
      if (failed.length) toast.error(`${message} No se validó: ${failed.join(", ")}.`);
      else toast.success(message);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
      changed();
    }
  }

  async function reloadSection(id: string) {
    try {
      const f = await get("orderItemResults", { params: { id } });
      const d = draftFrom(f);
      setResults((m) => ({ ...m, [id]: f }));
      setDrafts((m) => ({ ...m, [id]: d }));
      setBaselines((m) => ({ ...m, [id]: d }));
      changed();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  function fillSectionNormals(id: string) {
    const res = fillNormals(results[id]!, drafts[id] ?? {});
    setDrafts((m) => ({ ...m, [id]: res.draft }));
    setPrefilled((m) => ({ ...m, [id]: new Set([...(m[id] ?? []), ...res.filled]) }));
    toast.success(`${results[id]!.study_clave}: ${normalsMessage(res.filled.length)}`);
  }

  /** «Corregir resultado»: the validated values become editable in place, starting from what was validated. */
  function startCorrection(id: string) {
    setDrafts((m) => ({ ...m, [id]: baselines[id] ?? {} }));
    setCorrecting((m) => ({ ...m, [id]: "" }));
    setTimeout(() => {
      const group = scope.current?.querySelector(`[data-item="${id}"]`);
      if (group) focusFirstCaptureInput(group);
    }, 0);
  }

  /** Cancelar: back to the validated values, nothing sent. */
  function cancelCorrection(id: string) {
    setDrafts((m) => ({ ...m, [id]: baselines[id] ?? {} }));
    setCorrecting((m) => {
      const rest = { ...m };
      delete rest[id];
      return rest;
    });
    setFailures((m) => {
      const rest = { ...m };
      delete rest[id];
      return rest;
    });
  }

  /**
   * «Guardar corrección»: one `results.amend` with the changed values and the reason, the same
   * command any amendment uses. The study comes back `amended` and waits to be validated again.
   */
  async function saveCorrection(id: string) {
    const r = results[id]!;
    const payload = amendPayload(r, drafts[id] ?? {}, baselines[id] ?? {}, correcting[id] ?? "");
    if (!payload || correctionBlock(r, drafts[id] ?? {}, baselines[id] ?? {}, correcting[id] ?? "")) return;
    setBusy(`amend:${id}`);
    try {
      const updated = await run<"results.amend", OrderItemResults>("results.amend", payload, { branch_id: branch.id });
      const d = draftFrom(updated);
      setResults((m) => ({ ...m, [id]: updated }));
      setDrafts((m) => ({ ...m, [id]: d }));
      setBaselines((m) => ({ ...m, [id]: d }));
      setCorrecting((m) => {
        const rest = { ...m };
        delete rest[id];
        return rest;
      });
      setFailures((m) => {
        const rest = { ...m };
        delete rest[id];
        return rest;
      });
      toast.success(`${r.study_clave}: corrección guardada. Falta validarla para emitir el informe corregido.`);
      changed();
    } catch (e) {
      setFailures((m) => ({ ...m, [id]: errorMessage(e) }));
    } finally {
      setBusy(null);
    }
  }

  async function saveSection(id: string) {
    const clave = results[id]!.study_clave;
    setBusy(id);
    const ok = await saveOne(id);
    setBusy(null);
    if (ok) changed();
    if (ok) toast.success(`${clave} guardado.`);
    else toast.error(`${clave} no se guardó.`);
  }

  async function saveAll() {
    const ids = sections.map((s) => s.item.id).filter(needsSave);
    if (!ids.length) return;
    setBusy("all");
    let ok = 0;
    const failed: string[] = [];
    // One after the other: each command is its own transaction and refreshes the order's state.
    for (const id of ids) {
      if (await saveOne(id)) ok++;
      else failed.push(results[id]!.study_clave);
    }
    setBusy(null);
    if (ok) changed();
    const saved = ok ? `${plural(ok, "estudio guardado", "estudios guardados")}` : "";
    if (failed.length === 0) toast.success(`${saved}.`);
    else toast.error(`${saved ? `${saved}; ` : ""}no se guardó: ${failed.join(", ")}. Revisa el mensaje en su sección.`);
  }

  const pendingSaves = sections.filter((s) => needsSave(s.item.id)).length;
  const validatable = canValidate ? sections.map((s) => s.item.id).filter((id) => stepOf(id) !== null) : [];
  const anyValidated = sections.some((s) => {
    const st = results[s.item.id]?.state ?? s.item.state;
    return st === "validated" || st === "issued";
  });
  let filledTotal = 0;
  let capturableTotal = 0;
  for (const s of sections) {
    const r = results[s.item.id];
    if (!r || isLocked(r)) continue;
    filledTotal += filledCount(r, drafts[s.item.id] ?? {});
    capturableTotal += capturableAnalytes(r).length;
  }

  const facts = (
    <span className="row wrap">
      <span className="muted">
        {p.age_text} · {p.sex === "F" ? "Femenino" : "Masculino"} · {order.doctor_display} · {fmtDateTime(order.occurred_at)}
      </span>
      {order.balance_cents > 0 && <Chip tone="amber">Adeudo</Chip>}
    </span>
  );
  const delivery = anyValidated && (
    <Button variant="secondary" icon="send" onClick={() => navigate(`/entrega/${order.id}`)}>
      Entrega
    </Button>
  );

  return (
    <>
      {embedded ? (
        // Área de captura: the folio on top, as in the lab's previous system (Fernanda, 2026-11-02 20:41).
        <div className="capture-pane-head">
          <div className="grow">
            <div className="row" style={{ gap: 12, alignItems: "baseline" }}>
              <Link to={`/solicitudes/${order.id}`} className="folio">
                {order.folio}
              </Link>
              <span className="muted small">{plural(sections.length, "estudio", "estudios")}</span>
            </div>
            <h2 className="h2" style={{ margin: "4px 0" }}>
              {patientName}
            </h2>
            {facts}
          </div>
          {delivery}
        </div>
      ) : (
        <PageHead
          kicker={
            <>
              <Link to={`/solicitudes/${order.id}`} style={{ color: "inherit" }}>
                {order.folio}
              </Link>{" "}
              · {plural(sections.length, "estudio", "estudios")}
            </>
          }
          title={patientName}
          lede={facts}
          actions={
            <>
              <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/captura")}>
                Lista de trabajo
              </Button>
              {delivery}
            </>
          }
        />
      )}
      {sections.length === 0 ? (
        <Card>
          <Empty icon="flask" title="Esta solicitud no tiene estudios por capturar" />
        </Card>
      ) : (
        <div ref={scope} data-capture-scope="" className="stack-lg">
          {sections.map(({ item, sampleMissing }) => {
            const r = results[item.id];
            const inCorrection = item.id in correcting;
            const locked = r ? isLocked(r) && !inCorrection : true;
            const state = r?.state ?? item.state;
            const failure = failures[item.id];
            return (
              <div key={item.id} role="group" aria-label={`${item.study_clave} ${item.study_name}`} data-item={item.id} className={inCorrection ? "study-correcting" : undefined}>
                <Card
                  pad={false}
                  title={
                    <div className="row wrap" style={{ gap: 12 }}>
                      <span className="mono muted">{item.study_clave}</span>
                      <span style={{ fontWeight: 600 }}>{item.study_name}</span>
                      {sampleMissing && <Chip tone="red">Sin muestra</Chip>}
                      {inCorrection ? <Chip tone="violet">En corrección</Chip> : <Chip tone={itemTone(state)}>{ITEM_STATE_LABEL[state]}</Chip>}
                    </div>
                  }
                  actions={
                    r && !locked && hasEmptyOptionFields(r, drafts[item.id] ?? {}) ? (
                      <div className="row" style={{ gap: 8 }}>
                        {(
                          <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => fillSectionNormals(item.id)}>
                            Llenar normales
                          </Button>
                        )}
                      </div>
                    ) : undefined
                  }
                  footer={
                    // Save and validate side by side where the eye ends the study (Fernanda, 2026-11-02 21:12:
                    // "una para guardar y otra para validar"); the header keeps only «Llenar normales».
                    // A validated study ends with «Corregir resultado» instead (Fernanda, 2026-11-02 21:02).
                    r && inCorrection ? (
                      <CorrectionBar
                        reason={correcting[item.id] ?? ""}
                        block={correctionBlock(r, drafts[item.id] ?? {}, baselines[item.id] ?? {}, correcting[item.id] ?? "")}
                        busy={busy === `amend:${item.id}`}
                        disabled={busy !== null}
                        onReason={(v) => setCorrecting((m) => ({ ...m, [item.id]: v }))}
                        onCancel={() => cancelCorrection(item.id)}
                        onSave={() => void saveCorrection(item.id)}
                      />
                    ) : r && canBeCorrected(r) ? (
                      <ValidatedBar r={r} canCorrect={canValidate} disabled={busy !== null} onCorrect={() => startCorrection(item.id)} />
                    ) : r && !locked ? (
                      <div className="row study-actions">
                        {canValidate && r.state === "captured" && stepOf(item.id) === "validate" && (
                          <Button size="sm" variant="ghost" icon="refresh" disabled={busy !== null} onClick={() => setReturning(r)}>
                            Regresar a captura
                          </Button>
                        )}
                        <span className="grow" />
                        <Button size="sm" variant={canValidate ? "secondary" : "primary"} icon="check" busy={busy === item.id} disabled={busy !== null || !needsSave(item.id)} onClick={() => void saveSection(item.id)}>
                          Guardar
                        </Button>
                        {canValidate && (
                          <Button size="sm" variant="primary" icon="badge" busy={busy === `validate:${item.id}`} disabled={busy !== null || stepOf(item.id) === null} onClick={() => void validateSections([item.id], `validate:${item.id}`)}>
                            {validateLabel(r, drafts[item.id] ?? {}, baselines[item.id] ?? {})}
                          </Button>
                        )}
                      </div>
                    ) : undefined
                  }
                >
                  {failure && (
                    <div style={{ padding: "12px 18px 0" }}>
                      <Alert tone="red">{failure}</Alert>
                    </div>
                  )}
                  {!r ? (
                    <div style={{ padding: 18 }}>
                      <Alert tone="amber">
                        {sampleMissing === "rejected" ? "La muestra de este estudio fue rechazada; falta tomarla de nuevo." : "La muestra de este estudio no se ha registrado como tomada."}{" "}
                        <Link to="/muestras">Ir a Muestras pendientes</Link>
                      </Alert>
                    </div>
                  ) : (
                    <>
                      {inCorrection && (
                        <p className="correction-note">
                          Corrige los valores y escribe el motivo. El resultado validado y el informe ya emitido se conservan; la corrección se valida de nuevo y sale como informe corregido.
                        </p>
                      )}
                      {!inCorrection && r.state === "amended" && (
                        <p className="correction-note">
                          {canValidate
                            ? "Corrección guardada. Valídala para emitir el informe corregido; el informe anterior se conserva."
                            : "Corrección guardada. Falta que quien valida la revise y la valide para emitir el informe corregido."}
                        </p>
                      )}
                      <CaptureHead />
                      <CaptureRows
                        r={r}
                        draft={drafts[item.id] ?? {}}
                        locked={locked}
                        prefilled={prefilled[item.id]}
                        lowRed={lowRed}
                        onChange={(aid, v) => {
                          setDrafts((m) => ({ ...m, [item.id]: { ...m[item.id], [aid]: v } }));
                          setPrefilled((m) => (m[item.id] ? { ...m, [item.id]: without(m[item.id]!, aid) } : m));
                        }}
                      />
                    </>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      )}
      <div className={embedded ? "action-bar in-pane" : "action-bar"}>
        <span className="muted small">
          {plural(filledTotal, "valor capturado", "valores capturados")} de {capturableTotal} · <ColourLegend lowRed={lowRed} />
        </span>
        <span className="grow" />
        <span className="row action-bar-buttons">
        {sections.length > 0 && <DraftPreviewButton orderId={orderId} dirty={dirty} disabled={busy !== null} />}
        <Button variant={canValidate ? "secondary" : "primary"} icon="check" busy={busy === "all"} disabled={busy !== null || pendingSaves === 0} onClick={() => void saveAll()}>
          Guardar todo{pendingSaves > 0 ? ` (${pendingSaves})` : ""}
        </Button>
        {canValidate && (
          <Button variant="primary" icon="badge" busy={busy === "validate-all"} disabled={busy !== null || validatable.length === 0} onClick={() => void validateSections(validatable, "validate-all")} title="Valida y firma los estudios completos">
            Validar todo{validatable.length > 0 ? ` (${validatable.length})` : ""}
          </Button>
        )}
        </span>
      </div>
      <ReturnSheet item={returning} onClose={() => setReturning(null)} onDone={() => returning && void reloadSection(returning.order_item_id)} />
    </>
  );
}


/** A validated study's footer: who signed it, and «Corregir resultado» for whoever may validate. */
function ValidatedBar({ r, canCorrect, disabled, onCorrect }: { r: OrderItemResults; canCorrect: boolean; disabled: boolean; onCorrect: () => void }) {
  const v = r.validation;
  return (
    <div className="row wrap study-actions">
      <span className="muted small grow">
        {v ? `Validado por ${v.user_name}${v.cedula ? ` · Céd. ${v.cedula}` : ""} · ${fmtDateTime(v.occurred_at)}` : "Validado"}
        {!canCorrect && ". Solo quien valida puede corregirlo."}
      </span>
      {canCorrect && (
        <Button size="sm" variant="secondary" icon="pen" disabled={disabled} onClick={onCorrect}>
          Corregir resultado
        </Button>
      )}
    </div>
  );
}

/**
 * The footer of a study in correction: the reason (required, printed on the corrected report),
 * Cancelar and «Guardar corrección». Enter in the reason saves; Escape cancels.
 */
function CorrectionBar({
  reason,
  block,
  busy,
  disabled,
  onReason,
  onCancel,
  onSave,
}: {
  reason: string;
  block: string | null;
  busy: boolean;
  disabled: boolean;
  onReason: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="study-correction">
      <Field label="Motivo de la corrección" hint="Queda registrado y se imprime en el informe corregido.">
        <Input
          value={reason}
          maxLength={500}
          placeholder="Por ejemplo: valor mal transcrito"
          aria-required="true"
          onChange={(e) => onReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (!block && !disabled) onSave();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
        />
      </Field>
      <div className="row wrap study-actions">
        {block && <span className="muted small">{block}</span>}
        <span className="grow" />
        <Button size="sm" variant="ghost" disabled={disabled} onClick={onCancel}>
          Cancelar
        </Button>
        <Button size="sm" variant="primary" icon="check" busy={busy} disabled={disabled || block !== null} onClick={onSave}>
          Guardar corrección
        </Button>
      </div>
    </div>
  );
}
